<template>
  <b-modal
      id="client-dashboard-list-modal"
      v-model="visible"
      size="xl"
      centered
      hide-header
      hide-footer
      modal-class="cdl-modal"
      body-class="cdl-modal__body"
  >
    <div class="cdl">
      <!-- Hero header -->
      <div class="cdl__hero">
        <div class="cdl__hero-icon"><i :class="segIcon"></i></div>
        <div class="cdl__hero-text">
          <h4 class="cdl__hero-title">{{ title }}</h4>
          <span class="cdl__hero-sub">
            <b-spinner small v-if="loading" class="mr-50"/>
            <template v-else><strong>{{ total }}</strong> {{ $t('clientDashboard.clientsIn') }} {{ subtitle }}</template>
          </span>
        </div>
        <button type="button" class="cdl__close" @click="visible = false" aria-label="Close">
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>

      <b-overlay :show="loading" rounded opacity="0.4" spinner-variant="primary">
        <div class="cdl__table-wrap">
          <b-table
              responsive
              hover
              :items="items"
              :fields="fields"
              show-empty
              class="cdl-table mb-0"
              @row-clicked="openClient"
          >
            <template #cell(name)="data">
              <div class="cdl-name">
                <span class="cdl-name__main">{{ fullName(data.item) }}</span>
                <b-badge v-if="data.item.vip" variant="light-warning" pill class="ml-50">VIP</b-badge>
              </div>
              <small class="text-muted" v-if="data.item.accountId">#{{ data.item.accountId }}</small>
            </template>

            <template #cell(email)="data">
              <span class="cdl-ellipsis">{{ data.value || '—' }}</span>
            </template>

            <template #cell(country)="data">
              <span>{{ data.value || '—' }}</span>
            </template>

            <template #cell(representativeUserId)="data">
              <span>{{ data.value ? getUserFullName(data.value) : '—' }}</span>
            </template>

            <template #cell(ftdTime)="data">
              <span v-if="data.value" class="text-success font-weight-bold">{{ data.value | momentDate }}</span>
              <span v-else class="text-muted">—</span>
            </template>

            <template #cell(stdTime)="data">
              <span v-if="data.value" class="text-warning font-weight-bold">{{ data.value | momentDate }}</span>
              <span v-else class="text-muted">—</span>
            </template>

            <template #cell(totalDeposits)="data">
              <span class="font-weight-bold" :class="(data.value || 0) > 0 ? 'text-success' : 'text-muted'">
                <template v-if="data.item.currency">{{ convertToCurrency(data.value || 0, data.item.currency) }}</template>
                <template v-else>{{ data.value || 0 }}</template>
              </span>
            </template>

            <template #cell(lastNoteTime)="data">
              <div v-if="data.value">
                <span :class="noteAgeClass(data.value)">{{ data.value | momentDate }}</span>
                <small class="text-muted d-block cdl-ellipsis" v-if="data.item.lastNoteText"
                       v-b-tooltip.hover :title="data.item.lastNoteText">{{ data.item.lastNoteText }}</small>
              </div>
              <span v-else class="text-muted">—</span>
            </template>

            <template #cell(totalNotes)="data">
              <b-badge pill :variant="(data.value || 0) > 0 ? 'light-primary' : 'light-secondary'">
                <i class="fa-regular fa-note-sticky mr-25"></i>{{ data.value || 0 }}
              </b-badge>
            </template>

            <template #cell(open)="data">
              <b-button size="sm" variant="flat-primary" class="btn-icon"
                        v-b-tooltip.hover :title="$t('clientDashboard.openClient')"
                        @click.stop="openClient(data.item)">
                <i class="fa-solid fa-arrow-up-right-from-square"></i>
              </b-button>
            </template>

            <template #empty>
              <div class="text-center text-muted py-3">{{ $t('clientDashboard.noClients') }}</div>
            </template>
          </b-table>
        </div>

        <!-- Pagination -->
        <div class="cdl__footer" v-if="total > 0">
          <b-pagination
              v-model="page"
              :total-rows="total"
              :per-page="size"
              size="sm"
              class="mb-0"
              @change="onPageChange"
          />
          <div class="cdl__per-page">
            <b-form-select v-model="size" size="sm" :options="[25, 50, 100]" @change="onSizeChange" style="width: 80px"/>
            <span class="text-muted ml-50">/ {{ total }}</span>
          </div>
        </div>
      </b-overlay>
    </div>
  </b-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';

const SEG_ICON = {
  total: 'fa-solid fa-users',
  age0to15: 'fa-solid fa-clock',
  age15to30: 'fa-solid fa-hourglass-half',
  age30plus: 'fa-solid fa-triangle-exclamation',
  ftd: 'fa-solid fa-right-to-bracket',
  std: 'fa-solid fa-repeat',
  neverStd: 'fa-solid fa-user-clock',
};

export default {
  name: 'ClientDashboardListModal',
  mixins: [mixinBase, mixinNotifications],
  data: () => ({
    visible: false,
    loading: false,
    items: [],
    total: 0,
    page: 1,
    size: 25,
    segment: 'total',
    title: '',
    subtitle: '',
    ctx: { orgIds: [], buIds: [], buTypes: [], range: { from: null, until: null } },
  }),
  computed: {
    ...mapGetters('data', ['getUserFullName']),
    segIcon() {
      return SEG_ICON[this.segment] || 'fa-solid fa-users';
    },
    fields() {
      return [
        { key: 'name', label: this.$t('clientDashboard.client') },
        { key: 'email', label: this.$t('columns.email') },
        { key: 'country', label: this.$t('columns.country') },
        { key: 'representativeUserId', label: this.$t('columns.representative') },
        { key: 'ftdTime', label: this.$t('clientDashboard.ftd'), class: 'text-nowrap' },
        { key: 'stdTime', label: this.$t('clientDashboard.std'), class: 'text-nowrap' },
        { key: 'totalDeposits', label: this.$t('clientDashboard.totalDeposited'), class: 'text-right text-nowrap' },
        { key: 'lastNoteTime', label: this.$t('clientDashboard.lastNote') },
        { key: 'totalNotes', label: this.$t('clientDashboard.notes'), class: 'text-center' },
        { key: 'open', label: '', class: 'text-right' },
      ];
    },
  },
  methods: {
    ...mapActions('dashboard', ['getClientDashboardClients']),
    /**
     * @param {Object} p { segment, title, subtitle, orgIds, buIds, buTypes, range }
     */
    open(p) {
      this.segment = p.segment || 'total';
      this.title = p.title || this.$t('clientDashboard.client');
      this.subtitle = p.subtitle || '';
      this.ctx = {
        orgIds: p.orgIds || [],
        buIds: p.buIds || [],
        buTypes: p.buTypes || [],
        range: p.range || { from: null, until: null },
      };
      this.page = 1;
      this.visible = true;
      this.fetch();
    },
    fetch() {
      this.loading = true;
      this.getClientDashboardClients({
        segment: this.segment,
        orgIds: this.ctx.orgIds,
        buIds: this.ctx.buIds,
        buTypes: this.ctx.buTypes,
        range: this.ctx.range,
        pagination: { page: this.page, size: this.size },
      })
        .then(
          res => {
            this.items = Array.isArray(res.data) ? res.data : [];
            this.total = res.metadata && res.metadata.total != null ? res.metadata.total : this.items.length;
          },
          err => this.showServerErrorNotification('fetchDataError', 'clientDashboard', err)
        )
        .finally(() => { this.loading = false; });
    },
    onPageChange(p) {
      this.page = p;
      this.$nextTick(this.fetch);
    },
    onSizeChange() {
      this.page = 1;
      this.$nextTick(this.fetch);
    },
    fullName(item) {
      return [item.firstName, item.lastName].filter(Boolean).join(' ') || item.id;
    },
    noteAgeClass(time) {
      if (!time) return 'text-muted';
      const days = this.$moment().diff(this.$moment(time), 'days');
      if (days <= 15) return 'text-success';
      if (days <= 30) return 'text-warning';
      return 'text-danger';
    },
    openClient(item) {
      if (!item || !item.id) return;
      const route = this.$router.resolve({ name: 'client', params: { clientId: item.id } });
      window.open(route.href, '_blank');
    },
  },
};
</script>

<style lang="scss">
.cdl-modal {
  .modal-content { border: none; border-radius: 18px; overflow: hidden; box-shadow: 0 24px 60px -20px rgba(20, 24, 50, 0.45); }
  .modal-body.cdl-modal__body { padding: 0; }
}

.cdl {
  display: flex;
  flex-direction: column;

  &__hero {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.25rem 1.5rem;
    color: #fff;
    background: linear-gradient(135deg, #4da3ff 0%, #007BFF 55%, #0062cc 100%);
  }
  &__hero-icon {
    display: flex; align-items: center; justify-content: center;
    width: 48px; height: 48px; border-radius: 13px; font-size: 1.3rem;
    background: rgba(255, 255, 255, 0.22); flex-shrink: 0;
  }
  &__hero-text { flex: 1; min-width: 0; }
  &__hero-title { margin: 0; font-weight: 700; font-size: 1.2rem; }
  &__hero-sub { font-size: 0.85rem; opacity: 0.92; }
  &__close {
    border: none; background: rgba(255, 255, 255, 0.18); color: #fff;
    width: 34px; height: 34px; border-radius: 10px; font-size: 1rem; cursor: pointer;
    transition: background 0.2s ease, transform 0.2s ease; flex-shrink: 0;
    &:hover { background: rgba(255, 255, 255, 0.32); transform: rotate(90deg); }
  }

  &__table-wrap { max-height: 60vh; overflow-y: auto; }

  &__footer {
    display: flex; align-items: center; justify-content: space-between;
    padding: 0.85rem 1.25rem; border-top: 1px solid rgba(110, 117, 159, 0.12);
  }
  &__per-page { display: flex; align-items: center; }
}

.cdl-table {
  font-size: 0.85rem;
  thead th {
    background: #f7f9fc; color: #6e6b7b; text-transform: uppercase;
    font-size: 0.7rem; letter-spacing: 0.04em;
  }
  tbody tr { cursor: pointer; }
  .cdl-name__main { font-weight: 600; }
  .cdl-ellipsis {
    display: inline-block; max-width: 220px; overflow: hidden;
    text-overflow: ellipsis; white-space: nowrap; vertical-align: bottom;
  }
}

/* Dark mode */
body.dark-layout {
  .cdl-modal .modal-content { background: var(--crm-dark-surface, #232c3e); color: #d0d2d6; }
  .cdl__footer { border-top-color: rgba(255, 255, 255, 0.08); }
  .cdl-table thead th { background: var(--crm-dark-surface-2, #2e3850); color: #8b93ab; }
}
</style>
